import { Link, useParams } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { movies } from "@/mocks/movies";
import MovieCard from "@/components/base/MovieCard";

export default function MovieDetail() {
  const { id } = useParams();
  const movie = movies.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();

  useDocumentMeta({
    title: movie ? `${movie.title} - 午夜场的电影推荐与详情` : "影片未找到 - 午夜场的电影",
    description: movie
      ? `《${movie.title}》是一部${movie.year}年上映的${movie.genres.join("、")}午夜电影，评分${movie.rating}。${movie.summary}`
      : "未找到对应的午夜电影，请返回分类页浏览其他影片。",
    keywords: movie
      ? `${movie.title},${movie.genres.join(",")},午夜电影推荐,午夜场的电影`
      : "午夜场的电影",
  });

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center justify-center px-4 py-32 text-center md:px-6 lg:px-10">
        <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-200/70 text-3xl text-foreground-500">
          <i className="ri-film-line"></i>
        </span>
        <h1 className="mb-2 font-heading text-2xl font-extrabold text-foreground-50">
          影片不存在
        </h1>
        <p className="mb-6 text-sm text-foreground-500">
          这部午夜电影可能已被移除或链接有误，去分类页看看其他作品吧。
        </p>
        <Link
          to="/category"
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回电影分类
        </Link>
      </div>
    );
  }

  const related = movies
    .filter(
      (item) =>
        item.id !== movie.id &&
        (item.genres.some((genre) => movie.genres.includes(genre)) ||
          Math.abs(item.rating - movie.rating) < 0.6),
    )
    .slice(0, 4);

  const favorite = isFavorite(movie.id);

  return (
    <div className="w-full">
      <div className="relative w-full overflow-hidden">
        <div className="relative h-[320px] w-full md:h-[440px]">
          <img
            src={movie.backdrop}
            alt={`${movie.title} 主视觉 - 午夜电影详情`}
            title={`${movie.title} - 午夜场的电影推荐与详情`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/80 to-background-50/40" />
        </div>

        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6 lg:px-10">
          <div className="relative -mt-40 flex flex-col gap-6 md:-mt-56 md:flex-row md:items-end">
            <div className="w-40 shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-100 md:w-52">
              <img
                src={movie.poster}
                alt={`${movie.title} 电影海报 - 午夜场的电影`}
                title={`${movie.title} - 午夜电影海报`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="flex-1 pb-1">
              <nav className="mb-3 flex flex-wrap items-center gap-2 text-xs text-foreground-600">
                <Link to="/" className="transition-colors hover:text-primary-400">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to="/category" className="transition-colors hover:text-primary-400">
                  电影分类
                </Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-300">{movie.title}</span>
              </nav>
              <h1 className="mb-3 font-heading text-3xl font-extrabold tracking-tight text-foreground-50 md:text-4xl">
                {movie.title}
              </h1>
              <div className="mb-4 flex flex-wrap items-center gap-3 text-sm text-foreground-400">
                <span className="flex items-center gap-1 rounded-md bg-primary-500/15 px-2.5 py-1 font-semibold text-primary-400">
                  <i className="ri-star-fill"></i>
                  {movie.rating.toFixed(1)}
                </span>
                <span>{movie.year} 年</span>
                <span>{movie.region}</span>
                <span>{movie.duration} 分钟</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category?genre=${encodeURIComponent(genre)}`}
                    className="rounded-full bg-secondary-900/60 px-3 py-1 text-xs text-secondary-200 transition-colors hover:bg-secondary-700"
                  >
                    {genre}
                  </Link>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggleFavorite(movie.id)}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-3 text-sm font-semibold transition-colors ${
                favorite
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "border border-background-300 bg-background-100 text-foreground-200 hover:border-primary-500 hover:text-primary-400"
              }`}
            >
              <i className={favorite ? "ri-heart-fill" : "ri-heart-line"}></i>
              {favorite ? "已收藏" : "收藏本片"}
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
          <div>
            <section className="mb-10">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
                <i className="ri-movie-2-line text-primary-400"></i>
                影片简介
              </h2>
              <p className="text-sm leading-relaxed text-foreground-400">{movie.summary}</p>
            </section>

            <section className="mb-10">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
                <i className="ri-book-open-line text-primary-400"></i>
                剧情梗概
              </h2>
              <div className="space-y-4">
                {movie.synopsis.map((paragraph) => (
                  <p
                    key={paragraph.slice(0, 16)}
                    className="text-sm leading-loose text-foreground-400"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            <section className="mb-10">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
                <i className="ri-moon-clear-line text-primary-400"></i>
                午夜观影建议
              </h2>
              <div className="rounded-lg border-l-4 border-primary-500 bg-background-100 p-5">
                <p className="text-sm leading-loose text-foreground-400">{movie.tips}</p>
              </div>
            </section>

            <section>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
                <i className="ri-price-tag-3-line text-primary-400"></i>
                推荐标签
              </h2>
              <div className="flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-background-300 bg-background-100 px-3.5 py-1.5 text-xs font-medium text-foreground-300"
                  >
                    # {tag}
                  </span>
                ))}
              </div>
            </section>
          </div>

          <aside>
            <div className="sticky top-24 rounded-lg border border-background-200/80 bg-background-100 p-5">
              <h3 className="mb-4 font-heading text-base font-bold text-foreground-50">
                影片信息
              </h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="shrink-0 text-foreground-600">导演</dt>
                  <dd className="text-right text-foreground-200">{movie.director}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="shrink-0 text-foreground-600">上映年份</dt>
                  <dd className="text-right text-foreground-200">{movie.year}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="shrink-0 text-foreground-600">地区</dt>
                  <dd className="text-right text-foreground-200">{movie.region}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="shrink-0 text-foreground-600">片长</dt>
                  <dd className="text-right text-foreground-200">{movie.duration} 分钟</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="shrink-0 text-foreground-600">类型</dt>
                  <dd className="text-right text-foreground-200">{movie.genres.join(" / ")}</dd>
                </div>
                <div className="border-t border-background-200 pt-3">
                  <dt className="mb-2 text-foreground-600">主演</dt>
                  <dd className="flex flex-wrap gap-2">
                    {movie.cast.map((actor) => (
                      <span
                        key={actor}
                        className="rounded-full bg-background-200/70 px-2.5 py-1 text-xs text-foreground-300"
                      >
                        {actor}
                      </span>
                    ))}
                  </dd>
                </div>
              </dl>
              <Link
                to="/category"
                className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-200 transition-colors hover:border-primary-500 hover:text-primary-400"
              >
                <i className="ri-arrow-left-line"></i>
                返回分类页
              </Link>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-14 border-t border-background-200 pt-12">
            <h2 className="mb-6 flex items-center gap-2 font-heading text-2xl font-extrabold text-foreground-50">
              <i className="ri-links-line text-primary-400"></i>
              相关推荐
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {related.map((item, index) => (
                <MovieCard key={item.id} movie={item} index={index} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}